// ═══════════════════════════════════════════════════════════════
// SHELTER MANAGEMENT — Survivors List
// Searchable, filterable table of all survivors with discharge + CSV export
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState, useCallback } from 'react';
import { usePolling } from '../../hooks/usePolling';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, TextInput, Select, Table, Badge, Button, Group, Stack,
  Paper, Modal, Textarea, Loader, Center, Tooltip, ActionIcon,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconSearch, IconDownload, IconLogout, IconEye, IconArrowsExchange,
} from '@tabler/icons-react';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { formatPhone } from '../../lib/phoneFormat';
import { SURVIVOR_STATUS_COLORS } from '../../types/shelter';
import type { SurvivorStatus } from '../../types/shelter';

export default function Survivors() {
  const navigate = useNavigate();
  const { canEdit } = useModulePermission('shelter');
  const { activeIncident, user } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    survivors, shelters,
    survivorsLoading, sheltersLoading,
    loadSurvivors, loadShelters, updateSurvivor,
  } = useShelterStore();

  // ── Filter state ──────────────────────────────────────────
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [shelterFilter, setShelterFilter] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('lastName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // ── Discharge modal state ─────────────────────────────────
  const [dischargeTarget, setDischargeTarget] = useState<number | null>(null);
  const [dischargeReason, setDischargeReason] = useState('');
  const [dischargeLoading, setDischargeLoading] = useState(false);

  // ── Load data on mount ────────────────────────────────────
  useEffect(() => {
    loadSurvivors();
    loadShelters();
  }, [incidentId, loadSurvivors, loadShelters]);
  usePolling(() => { loadSurvivors(); loadShelters(); });

  // Store already server-side filters by active incident; use directly
  const iSurvivors = incidentId ? survivors : [];
  const iShelters = incidentId ? shelters : [];

  // ── Shelter lookup map ────────────────────────────────────
  const shelterMap = useMemo(() => {
    const m: Record<string, string> = {};
    shelters.forEach(s => { m[String(s.id)] = s.name; });
    return m;
  }, [shelters]);

  // ── Shelter select data ───────────────────────────────────
  const shelterSelectData = useMemo(
    () => iShelters.map(s => ({ value: String(s.id), label: s.name })),
    [iShelters],
  );

  // ── Filtered results ──────────────────────────────────────
  const filtered = useMemo(() => {
    let list = iSurvivors;

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        (s.firstName || '').toLowerCase().includes(q) ||
        (s.lastName || '').toLowerCase().includes(q) ||
        (s.phone || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q),
      );
    }

    if (statusFilter) {
      list = list.filter(s => s.status === statusFilter);
    }

    if (shelterFilter) {
      list = list.filter(s => s.shelterId === shelterFilter);
    }

    // Sort
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = (a as any)[sortField] || '';
      const vb = (b as any)[sortField] || '';
      return String(va).localeCompare(String(vb)) * dir;
    });
    return list;
  }, [iSurvivors, search, statusFilter, shelterFilter, sortField, sortDir]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, statusFilter, shelterFilter, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Discharge handler ─────────────────────────────────────
  const handleDischarge = useCallback(async () => {
    if (!dischargeTarget || !dischargeReason.trim()) return;
    setDischargeLoading(true);
    try {
      const actorName = user?.firstName
        ? `${user.firstName} ${user.lastName || ''}`.trim()
        : 'Unknown';
      await updateSurvivor(dischargeTarget, {
        status: 'discharged',
        dischargeTimestamp: new Date().toISOString(),
        dischargeReason: dischargeReason.trim(),
        dischargedBy: actorName,
      });
      notifications.show({ title: 'Discharged', message: 'Survivor has been discharged.', color: 'green' });
      setDischargeTarget(null);
      setDischargeReason('');
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to discharge survivor.',
        color: 'red',
      });
    } finally {
      setDischargeLoading(false);
    }
  }, [dischargeTarget, dischargeReason, user, updateSurvivor]);

  // ── CSV Export ─────────────────────────────────────────────
  const handleExport = useCallback(() => {
    if (filtered.length === 0) return;
    const headers = ['Name', 'Status', 'Shelter', 'Cot', 'Phone', 'Email', 'Intake Date', 'DOB', 'Gender', 'Home Address'];
    const rows = filtered.map(s => [
      `${s.firstName} ${s.lastName}`,
      s.status,
      shelterMap[s.shelterId] || s.shelterId || '',
      s.cotAssignment,
      s.phone,
      s.email,
      s.intakeTimestamp ? new Date(s.intakeTimestamp).toLocaleDateString() : '',
      s.dateOfBirth,
      s.gender,
      [s.homeAddress, s.city, s.state, s.zip].filter(Boolean).join(', '),
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `survivors_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filtered, shelterMap]);

  // ── Loading state ─────────────────────────────────────────
  const isLoading = survivorsLoading || sheltersLoading;
  if (isLoading && iSurvivors.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  return (
    <Stack gap="md">
      {/* ── Header ─────────────────────────────────────────── */}
      <Group justify="space-between">
        <Group gap="xs">
          <Title order={3} c="black">Survivors</Title>
          <Badge size="sm" variant="light" color="blue">
            {filtered.length} of {iSurvivors.length}
          </Badge>
        </Group>
        <Button
          leftSection={<IconDownload size={14} />}
          variant="light"
          size="sm"
          onClick={handleExport}
          disabled={filtered.length === 0}
        >
          Export CSV
        </Button>
      </Group>

      {/* ── Filters ────────────────────────────────────────── */}
      <Paper p="md" radius="sm" withBorder>
        <Group gap="md" wrap="wrap">
          <TextInput
            placeholder="Search name, phone, email..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
          <Select
            placeholder="All Statuses"
            value={statusFilter}
            onChange={setStatusFilter}
            data={[
              { value: 'pre-registered', label: 'Pre-Registered' },
              { value: 'checked_in', label: 'Checked In' },
              { value: 'transferred', label: 'Transferred' },
              { value: 'discharged', label: 'Discharged' },
              { value: 'missing', label: 'Missing' },
            ]}
            clearable
            size="sm"
            w={160}
          />
          <Select
            placeholder="All Shelters"
            value={shelterFilter}
            onChange={setShelterFilter}
            data={shelterSelectData}
            clearable
            searchable
            size="sm"
            w={200}
          />
        </Group>
      </Paper>

      {/* ── Table ──────────────────────────────────────────── */}
      <Paper radius="sm" withBorder style={{ overflow: 'auto', maxHeight: 500 }}>
        <Table highlightOnHover striped stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('lastName')}>Name{sortIcon('lastName')}</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('shelterId')}>Shelter{sortIcon('shelterId')}</Table.Th>
              <Table.Th>Cot</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('phone')}>Phone{sortIcon('phone')}</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('intakeDate')}>Intake Date{sortIcon('intakeDate')}</Table.Th>
              <Table.Th ta="center">Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text ta="center" c="dimmed" py="lg">
                    {isLoading ? 'Loading...' : 'No survivors found.'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              pageRows.map(s => (
                <Table.Tr
                  key={s.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/shelter/survivor/${s.id}`)}
                >
                  <Table.Td>
                    <Text size="sm" fw={500}>{s.lastName}, {s.firstName}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      color={SURVIVOR_STATUS_COLORS[s.status as SurvivorStatus] ?? 'gray'}
                    >
                      {s.status ? s.status.replace(/_/g, ' ') : 'checked in'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{shelterMap[s.shelterId] || '\u2014'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{s.cotAssignment || '\u2014'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{s.phone ? formatPhone(s.phone) : '\u2014'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {s.intakeTimestamp ? new Date(s.intakeTimestamp).toLocaleDateString() : '\u2014'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="center" onClick={e => e.stopPropagation()}>
                      <Tooltip label="View Detail">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => navigate(`/shelter/survivor/${s.id}`)}
                        >
                          <IconEye size={14} />
                        </ActionIcon>
                      </Tooltip>
                      {canEdit && s.status === 'checked_in' && (
                        <Tooltip label="Transfer">
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            size="sm"
                            onClick={() => navigate('/shelter/transfers')}
                          >
                            <IconArrowsExchange size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {canEdit && s.status === 'checked_in' && (
                        <Tooltip label="Discharge">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            onClick={() => {
                              setDischargeTarget(s.id);
                              setDischargeReason('');
                            }}
                          >
                            <IconLogout size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* ── Pagination ─────────────────────────────────────────── */}
      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* ── Discharge Modal ──────────────────────────────────── */}
      <Modal
        opened={dischargeTarget !== null}
        onClose={() => { setDischargeTarget(null); setDischargeReason(''); }}
        title="Discharge Survivor"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Provide a reason for discharge. The survivor's status will be set to <Badge size="xs" color="gray">discharged</Badge>.
          </Text>
          <Textarea
            label="Discharge Reason"
            placeholder="e.g., Returned home, transferred to family, relocated..."
            value={dischargeReason}
            onChange={e => setDischargeReason(e.target.value)}
            minRows={3}
            required
          />
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => { setDischargeTarget(null); setDischargeReason(''); }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDischarge}
              loading={dischargeLoading}
              disabled={!dischargeReason.trim()}
            >
              Confirm Discharge
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
